/**
 * RequirementsFetchOrchestrator
 *
 * Coordinates automatic fetching of college requirements on first visit:
 * - Deadlines (ED, EA, RD, financial aid)
 * - Essay requirements (prompts, word limits, types)
 * - Application components (transcripts, test scores, recommendations)
 *
 * Tracks fetch progress and notifies when ready for student verification.
 */

import { supabase } from '../lib/supabase.js';
import AdvancedEssayPromptService from './AdvancedEssayPromptService.js';

class RequirementsFetchOrchestrator {
  /**
   * Start fetching all requirements for a college
   * @param {string} collegeId - college_journey_colleges.id
   * @param {string} collegeName - Name of the college
   * @param {string} userId - User ID for tracking
   * @param {number} graduationYear - Student's graduation year for correct admission cycle
   * @returns {Promise<object>} Fetch result with status and data
   */
  async startFetch(collegeId, collegeName, userId, graduationYear) {
    console.log(`[RequirementsFetchOrchestrator] Starting fetch for ${collegeName}`, {
      collegeId,
      collegeName,
      userId,
      graduationYear,
      collegeNameType: typeof collegeName,
      collegeNameValid: !!(collegeName && typeof collegeName === 'string')
    });

    // Validate parameters
    if (!collegeName || typeof collegeName !== 'string') {
      console.error('[RequirementsFetchOrchestrator] ❌ Invalid collegeName parameter:', collegeName);
      return {
        success: false,
        error: 'Invalid college name provided - cannot fetch requirements'
      };
    }

    try {
      // Update status to in_progress
      await this.updateFetchStatus(collegeId, {
        requirements_fetch_status: 'in_progress',
        requirements_fetch_started_at: new Date().toISOString(),
        requirements_ready_for_verification: false
      });

      // Fetch all requirements in parallel for speed
      const fetchResults = await Promise.allSettled([
        this.fetchDeadlines(collegeName, graduationYear),
        this.fetchEssayRequirements(collegeName, graduationYear),
        this.fetchApplicationComponents(collegeName, graduationYear)
      ]);

      const [deadlinesResult, essaysResult, componentsResult] = fetchResults;

      // Extract data and confidence scores
      const deadlines = deadlinesResult.status === 'fulfilled' ? deadlinesResult.value : null;
      const essays = essaysResult.status === 'fulfilled' ? essaysResult.value : null;
      const components = componentsResult.status === 'fulfilled' ? componentsResult.value : null;

      // Calculate overall confidence score
      const confidenceScore = this.calculateOverallConfidence({
        deadlines,
        essays,
        components
      });

      // Save fetched data to database
      await this.saveFetchedData(collegeId, {
        deadlines,
        essays,
        components,
        confidenceScore
      });

      // Mark fetch as completed
      await this.updateFetchStatus(collegeId, {
        requirements_fetch_status: 'completed',
        requirements_fetch_completed_at: new Date().toISOString(),
        requirements_ready_for_verification: true,
        requirements_fetch_confidence: confidenceScore
      });

      console.log(`[RequirementsFetchOrchestrator] ✅ Fetch completed for ${collegeName} (confidence: ${confidenceScore})`);

      return {
        success: true,
        data: { deadlines, essays, components },
        confidence: confidenceScore
      };

    } catch (error) {
      console.error(`[RequirementsFetchOrchestrator] ❌ Fetch failed for ${collegeName}:`, error);

      // Mark fetch as failed
      await this.updateFetchStatus(collegeId, {
        requirements_fetch_status: 'failed',
        requirements_fetch_completed_at: new Date().toISOString()
      });

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Fetch college deadlines
   */
  async fetchDeadlines(collegeName, graduationYear) {
    // Calculate admission cycle:
    // graduationYear 2026 = enter college Fall 2026 = apply during 2025-2026 cycle
    const applicationYear = graduationYear ? graduationYear - 1 : null;
    const admissionCycle = graduationYear ? `${applicationYear}-${graduationYear}` : null;

    console.log(`[RequirementsFetchOrchestrator] Fetching deadlines for ${collegeName}`, {
      graduationYear,
      applicationYear,
      admissionCycle: admissionCycle || 'Not specified',
      collegeEntry: graduationYear ? `Fall ${graduationYear}` : 'Not specified'
    });

    // Validate collegeName parameter
    if (!collegeName || typeof collegeName !== 'string') {
      console.error('[RequirementsFetchOrchestrator] Invalid collegeName:', collegeName);
      return {
        success: false,
        error: 'Invalid college name provided',
        confidence: 0
      };
    }

    try {
      // 🆕 FIRST: Check scraper cache (BigFuture scraper)
      const scraperData = await this.checkScraperCache(collegeName, 'deadlines');
      if (scraperData) {
        console.log(`[RequirementsFetchOrchestrator] ✅ Using scraper cache for ${collegeName} deadlines (confidence: ${scraperData.confidence})`);
        return scraperData;
      }

      console.log(`[RequirementsFetchOrchestrator] No scraper cache found, falling back to AI fetch`);

      // FALLBACK: Call existing AI service to fetch deadlines
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'fetch-college-data',
          collegeName: collegeName,
          dataFields: 'deadlines',
          graduationYear: graduationYear,
          applicationYear: applicationYear,
          admissionCycle: admissionCycle
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch deadlines: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        return {
          success: true,
          data: result.data,
          confidence: result.confidence || 0.8,
          source: 'ai_fetch'
        };
      }

      throw new Error('No deadline data returned');

    } catch (error) {
      console.error('[RequirementsFetchOrchestrator] Deadline fetch error:', error);
      return {
        success: false,
        error: error.message,
        confidence: 0
      };
    }
  }

  /**
   * Fetch essay requirements using AdvancedEssayPromptService
   */
  async fetchEssayRequirements(collegeName, graduationYear) {
    // Calculate admission cycle:
    // graduationYear 2026 = enter college Fall 2026 = apply during 2025-2026 cycle
    const applicationYear = graduationYear ? graduationYear - 1 : null;
    const admissionCycle = graduationYear ? `${applicationYear}-${graduationYear}` : null;

    console.log(`[RequirementsFetchOrchestrator] Fetching essay requirements for ${collegeName}`, {
      graduationYear,
      applicationYear,
      admissionCycle: admissionCycle || 'Not specified',
      collegeEntry: graduationYear ? `Fall ${graduationYear}` : 'Not specified'
    });

    // Validate collegeName parameter
    if (!collegeName || typeof collegeName !== 'string') {
      console.error('[RequirementsFetchOrchestrator] Invalid collegeName:', collegeName);
      return {
        success: false,
        error: 'Invalid college name provided',
        confidence: 0
      };
    }

    try {
      // 🆕 FIRST: Check scraper cache for essay URLs
      const scraperEssayUrl = await this.checkScraperCache(collegeName, 'essay_url');

      if (scraperEssayUrl?.data?.url) {
        console.log(`[RequirementsFetchOrchestrator] ✅ Found essay URL in scraper cache: ${scraperEssayUrl.data.url}`);
        console.log(`[RequirementsFetchOrchestrator] Using AI to extract essay prompts from URL...`);

        // Use AI to extract essay prompts from the discovered URL
        const result = await AdvancedEssayPromptService.fetchEssayPrompts(collegeName, {
          additionalParams: {
            action: 'fetch-essay-prompts',
            graduationYear: graduationYear,
            applicationYear: applicationYear,
            admissionCycle: admissionCycle,
            essayPageUrl: scraperEssayUrl.data.url, // Provide the URL to the AI
            searchVariations: [
              collegeName,
              collegeName.replace(/University/g, '').trim(),
              collegeName.replace(/College/g, '').trim()
            ].filter(name => name.length > 2)
          }
        });

        const formatted = AdvancedEssayPromptService.formatResultForUI(result);

        if (formatted.success && formatted.essays.length > 0) {
          return {
            success: true,
            data: {
              platform: 'common_app',
              personalStatementRequired: true,
              essays: formatted.essays.map(essay => ({
                prompt: essay.prompt,
                word_limit: essay.word_limit,
                type: essay.type,
                required: essay.required
              })),
              additionalInfo: `Essays sourced from ${formatted.source} (URL discovered by scraper)`,
              confidence_score: formatted.confidence / 100,
              primary_source: formatted.source.toLowerCase().replace(/\\s+/g, '_'),
              prompt_year: formatted.year || '2025-2026'
            },
            confidence: formatted.confidence / 100,
            source: `${formatted.source} (scraper-assisted)`
          };
        }
      }

      console.log(`[RequirementsFetchOrchestrator] No scraper essay URL found, using standard AI search`);

      // FALLBACK: Use standard AI search without URL hint
      const result = await AdvancedEssayPromptService.fetchEssayPrompts(collegeName, {
        additionalParams: {
          action: 'fetch-essay-prompts',
          graduationYear: graduationYear,
          applicationYear: applicationYear,
          admissionCycle: admissionCycle,
          searchVariations: [
            collegeName,
            collegeName.replace(/University/g, '').trim(),
            collegeName.replace(/College/g, '').trim()
          ].filter(name => name.length > 2)
        }
      });

      const formatted = AdvancedEssayPromptService.formatResultForUI(result);

      if (formatted.success && formatted.essays.length > 0) {
        return {
          success: true,
          data: {
            platform: 'common_app',
            personalStatementRequired: true,
            essays: formatted.essays.map(essay => ({
              prompt: essay.prompt,
              word_limit: essay.word_limit,
              type: essay.type,
              required: essay.required
            })),
            additionalInfo: `Essays sourced from ${formatted.source}`,
            confidence_score: formatted.confidence / 100,
            primary_source: formatted.source.toLowerCase().replace(/\\s+/g, '_'),
            prompt_year: formatted.year || '2025-2026'
          },
          confidence: formatted.confidence / 100,
          source: formatted.source
        };
      }

      return {
        success: false,
        error: 'No essays found',
        confidence: 0
      };

    } catch (error) {
      console.error('[RequirementsFetchOrchestrator] Essay fetch error:', error);
      return {
        success: false,
        error: error.message,
        confidence: 0
      };
    }
  }

  /**
   * Fetch application components (standard requirements)
   */
  async fetchApplicationComponents(collegeName, graduationYear) {
    // Calculate admission cycle:
    // graduationYear 2026 = enter college Fall 2026 = apply during 2025-2026 cycle
    const applicationYear = graduationYear ? graduationYear - 1 : null;
    const admissionCycle = graduationYear ? `${applicationYear}-${graduationYear}` : null;

    console.log(`[RequirementsFetchOrchestrator] Fetching application components for ${collegeName}`, {
      graduationYear,
      applicationYear,
      admissionCycle: admissionCycle || 'Not specified',
      collegeEntry: graduationYear ? `Fall ${graduationYear}` : 'Not specified'
    });

    // Validate collegeName parameter
    if (!collegeName || typeof collegeName !== 'string') {
      console.error('[RequirementsFetchOrchestrator] Invalid collegeName:', collegeName);
      // Return standard components as fallback even with invalid name
      return this.getStandardApplicationComponents();
    }

    // Most colleges have standard Common App requirements
    // We can fetch specific details from AI if needed, but default components are known
    try {
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'fetch-college-data',
          collegeName: collegeName,
          dataFields: 'application_requirements',
          graduationYear: graduationYear,
          applicationYear: applicationYear,
          admissionCycle: admissionCycle
        })
      });

      if (!response.ok) {
        // Use standard defaults if fetch fails
        return this.getStandardApplicationComponents();
      }

      const result = await response.json();

      if (result.success && result.data) {
        return {
          success: true,
          data: result.data,
          confidence: result.confidence || 0.9,
          source: 'ai_fetch'
        };
      }

      // Fallback to standard components
      return this.getStandardApplicationComponents();

    } catch (error) {
      console.error('[RequirementsFetchOrchestrator] Components fetch error:', error);
      // Use standard defaults
      return this.getStandardApplicationComponents();
    }
  }

  /**
   * Get standard Common App components (fallback)
   */
  getStandardApplicationComponents() {
    return {
      success: true,
      data: {
        common_app_required: true,
        transcripts_required: true,
        test_scores_optional: true, // Most schools are test-optional now
        teacher_recommendations: 2,
        counselor_recommendation: true,
        application_fee: 75, // Common average
        mid_year_report: true
      },
      confidence: 0.9, // High confidence for standard requirements
      source: 'standard_defaults'
    };
  }

  /**
   * Calculate overall confidence score from all fetches
   */
  calculateOverallConfidence({ deadlines, essays, components }) {
    const scores = [];

    if (deadlines?.success) scores.push(deadlines.confidence || 0);
    if (essays?.success) scores.push(essays.confidence || 0);
    if (components?.success) scores.push(components.confidence || 0);

    if (scores.length === 0) return 0;

    // Weighted average (essays are most important)
    const weights = [0.3, 0.5, 0.2]; // deadlines, essays, components
    const weightedScores = scores.map((score, idx) => score * (weights[idx] || 0.3));
    const totalWeight = weights.slice(0, scores.length).reduce((a, b) => a + b, 0);

    return Math.round((weightedScores.reduce((a, b) => a + b, 0) / totalWeight) * 100) / 100;
  }

  /**
   * Save fetched data to database
   */
  async saveFetchedData(collegeId, { deadlines, essays, components, confidenceScore }) {
    console.log(`[RequirementsFetchOrchestrator] Saving fetched data for college ${collegeId}`);

    try {
      // Get current college record
      const { data: college, error: fetchError } = await supabase
        .from('college_journey_colleges')
        .select('global_college_id')
        .eq('id', collegeId)
        .single();

      if (fetchError) throw fetchError;

      const updates = {};

      // Save essay requirements to college_journey_colleges
      if (essays?.success && essays.data) {
        updates.essay_requirements = essays.data;
      }

      // Save deadlines to global_colleges if we have global_college_id
      if (college?.global_college_id && deadlines?.success && deadlines.data) {
        const { error: globalError } = await supabase
          .from('global_colleges')
          .update({
            ed_deadline: deadlines.data.ed_deadline,
            ea_deadline: deadlines.data.ea_deadline,
            rd_deadline: deadlines.data.rd_deadline,
            ed2_deadline: deadlines.data.ed2_deadline,
            finaid_deadline: deadlines.data.finaid_deadline,
            data_last_updated: new Date().toISOString()
          })
          .eq('id', college.global_college_id);

        if (globalError) {
          console.warn('[RequirementsFetchOrchestrator] Failed to update global deadlines:', globalError);
        }
      }

      // Update college_journey_colleges with any local overrides
      if (Object.keys(updates).length > 0) {
        const { error: updateError } = await supabase
          .from('college_journey_colleges')
          .update(updates)
          .eq('id', collegeId);

        if (updateError) {
          console.error('[RequirementsFetchOrchestrator] Failed to save data:', updateError);
          throw updateError;
        }
      }

      console.log(`[RequirementsFetchOrchestrator] ✅ Data saved successfully`);

    } catch (error) {
      console.error('[RequirementsFetchOrchestrator] Error saving data:', error);
      // Don't throw - we still want to mark fetch as completed even if save fails
    }
  }

  /**
   * Update fetch status in database
   */
  async updateFetchStatus(collegeId, updates) {
    try {
      const { error } = await supabase
        .from('college_journey_colleges')
        .update(updates)
        .eq('id', collegeId);

      if (error) throw error;

    } catch (error) {
      console.error('[RequirementsFetchOrchestrator] Error updating fetch status:', error);
      throw error;
    }
  }

  /**
   * Check if fetch is needed for a college
   */
  async shouldFetch(collegeId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select('requirements_fetch_status, requirements_verified')
        .eq('id', collegeId)
        .single();

      if (error) throw error;

      // Fetch if never started or failed (and not verified)
      return (!data.requirements_fetch_status ||
              data.requirements_fetch_status === 'not_started' ||
              data.requirements_fetch_status === 'failed') &&
             !data.requirements_verified;

    } catch (error) {
      console.error('[RequirementsFetchOrchestrator] Error checking fetch status:', error);
      return true; // Default to fetching if we can't determine status
    }
  }

  /**
   * Check scraper cache (scrape_history) for pre-extracted data
   * Returns data if found, null otherwise
   *
   * @param {string} collegeName - College name to search for
   * @param {string} dataType - Type of data to retrieve ('deadlines', 'essay_url', 'requirements_url')
   * @returns {Promise<object|null>} Scraped data or null
   */
  async checkScraperCache(collegeName, dataType) {
    try {
      console.log(`[RequirementsFetchOrchestrator] Checking scraper cache for ${collegeName} (${dataType})`);

      // Query scrape_history for the most recent successful scrape of this college
      const { data, error } = await supabase
        .from('scrape_history')
        .select('*')
        .ilike('school_name', collegeName)
        .eq('scrape_status', 'success')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No results found
          console.log(`[RequirementsFetchOrchestrator] No scraper cache found for ${collegeName}`);
          return null;
        }
        throw error;
      }

      if (!data) {
        console.log(`[RequirementsFetchOrchestrator] No scraper cache found for ${collegeName}`);
        return null;
      }

      console.log(`[RequirementsFetchOrchestrator] Found scraper cache for ${collegeName} (scraped ${data.created_at})`);

      // Extract requested data type
      if (dataType === 'deadlines') {
        // Return deadlines if available
        if (data.deadlines && data.deadlines.length > 0) {
          // Convert BigFuture deadlines format to expected format
          const deadlineData = {
            ed_deadline: null,
            ed2_deadline: null,
            ea_deadline: null,
            rd_deadline: null,
            finaid_deadline: null
          };

          data.deadlines.forEach(d => {
            if (d.plan === 'ED I' || d.plan === 'ED') {
              deadlineData.ed_deadline = d.date_iso;
            } else if (d.plan === 'ED II') {
              deadlineData.ed2_deadline = d.date_iso;
            } else if (d.plan === 'EA') {
              deadlineData.ea_deadline = d.date_iso;
            } else if (d.plan === 'RD') {
              deadlineData.rd_deadline = d.date_iso;
            }
          });

          return {
            success: true,
            data: deadlineData,
            confidence: data.overall_confidence || 0.9,
            source: 'bigfuture_scraper',
            scrapedAt: data.created_at
          };
        }
      } else if (dataType === 'essay_url') {
        // Return essay URL if available
        if (data.essays && data.essays.url) {
          return {
            success: true,
            data: {
              url: data.essays.url,
              confidence: data.essays.confidence || 0.8
            },
            confidence: data.essays.confidence || 0.8,
            source: 'bigfuture_scraper',
            scrapedAt: data.created_at
          };
        }
      } else if (dataType === 'requirements_url') {
        // Return requirements URL if available
        if (data.requirements && data.requirements.url) {
          return {
            success: true,
            data: {
              url: data.requirements.url,
              confidence: data.requirements.confidence || 0.8
            },
            confidence: data.requirements.confidence || 0.8,
            source: 'bigfuture_scraper',
            scrapedAt: data.created_at
          };
        }
      }

      console.log(`[RequirementsFetchOrchestrator] Scraper cache found but no ${dataType} data available`);
      return null;

    } catch (error) {
      console.error('[RequirementsFetchOrchestrator] Error checking scraper cache:', error);
      return null; // Don't fail the whole fetch if cache check fails
    }
  }
}

export default new RequirementsFetchOrchestrator();
